// Project save/load against localStorage. Snapshots only the data we can't
// rebuild from modules — tempo, pattern bank, and per-channel mixer state.
// Audio nodes themselves are recreated by re-registering modules at boot.

const KEY_PREFIX = 'music-maker:project:';
const LIST_KEY = 'music-maker:projects';

// Deep-clone any nested data we capture from the live state so the snapshot
// can't be mutated by subsequent edits — critical for undo/redo correctness.
const clone = (v) => (typeof structuredClone === 'function'
    ? structuredClone(v)
    : JSON.parse(JSON.stringify(v)));

export function snapshotProject(ctx, name) {
    const patterns = ctx.patterns.list().map(p => clone({
        id: p.id,
        name: p.name,
        length: p.length,
        tracks: p.tracks,
    }));
    const channels = ctx.mixer.all().map(c => ({
        id: c.id,
        name: c.name,
        color: c.color,
        volume: c.getVolume(),
        pan: c.getPan(),
        muted: c.isMuted(),
        solo: c.isSolo(),
        eqLow: c.getEqLow(),
        eqMid: c.getEqMid(),
        eqHigh: c.getEqHigh(),
        inserts: c.listInserts().map(ins => ({
            libId: ins.libId,
            params: ins.instance.params.map(p => ({ label: p.label, value: p.get() })),
        })),
        // Per-channel post-fader send levels (dB) keyed by FX channel id.
        // -Infinity is serialised as null and restored as -Infinity below.
        sends: c.listSends().reduce((acc, fxId) => {
            const lvl = c.getSendLevel(fxId);
            acc[fxId] = isFinite(lvl) ? lvl : null;
            return acc;
        }, {}),
    }));
    return {
        name,
        version: 1,
        bpm: ctx.scheduler.getBPM(),
        activePatternId: ctx.patterns.getActiveId(),
        patterns,
        channels,
        song: {
            mode: ctx.song.getMode(),
            clips: clone(ctx.song.getClips()),
            audioTracks: clone(ctx.song.getAudioTracks()),
        },
    };
}

export function applySnapshot(ctx, snap) {
    if (!snap) return;
    if (snap.bpm) ctx.scheduler.setBPM(snap.bpm);
    if (Array.isArray(snap.patterns)) {
        // Wholesale replace — preserve each pattern's id so activePatternId
        // resolves. Deep-clone so the live pattern bank never shares
        // references with the history stack.
        const restored = snap.patterns.map(p => clone({
            id: p.id,
            name: p.name,
            length: p.length,
            tracks: p.tracks ?? {},
        }));
        ctx.patterns.replaceAll(restored, snap.activePatternId);
    }
    if (Array.isArray(snap.channels)) {
        for (const c of snap.channels) {
            const ch = ctx.mixer.get(c.id);
            if (!ch) continue;
            if (c.name) { ch.setName(c.name); ctx.lanes.setName(c.id, c.name); }
            if (c.color) { ch.setColor(c.color); ctx.lanes.setColor(c.id, c.color); }
            ch.setVolume(c.volume);
            ch.setPan(c.pan);
            ch.setMuted(c.muted);
            ch.setSolo(c.solo);
            if (c.eqLow !== undefined) ch.setEqLow(c.eqLow);
            if (c.eqMid !== undefined) ch.setEqMid(c.eqMid);
            if (c.eqHigh !== undefined) ch.setEqHigh(c.eqHigh);
            // Sends: re-apply per-FX-channel send levels.
            if (c.sends && typeof c.sends === 'object') {
                for (const [fxId, lvl] of Object.entries(c.sends)) {
                    const dB = lvl == null ? -Infinity : lvl;
                    try { ch.addSend(fxId, dB); } catch (_) { /* fx channel missing */ }
                }
            }
            // Inserts: drop existing, recreate from snapshot, apply param values.
            ch.clearInserts();
            if (Array.isArray(c.inserts)) {
                for (const ins of c.inserts) {
                    const insertId = ch.addInsert(ins.libId);
                    if (!Array.isArray(ins.params)) continue;
                    const created = ch.listInserts().find(i => i.id === insertId);
                    if (!created) continue;
                    for (const saved of ins.params) {
                        const p = created.instance.params.find(x => x.label === saved.label);
                        if (p) p.set(saved.value);
                    }
                }
            }
        }
    }
    if (snap.song) {
        // Backward-compat: older snapshots stored a `slots` array of
        // { patternId } where each slot meant "play once". Migrate to clips.
        let clips = snap.song.clips;
        if (!clips && Array.isArray(snap.song.slots)) {
            clips = snap.song.slots.map((s, i) => ({
                patternId: s.patternId, startBar: i, lengthBars: 1,
            }));
        }
        ctx.song.replaceAll(clips || []);
        if (snap.song.mode) ctx.song.setMode(snap.song.mode);

        // Restore audio tracks. Hydration of the actual Tone.Player buffers
        // happens lazily in main.js (it owns the IDB read path).
        ctx.song.replaceAudioTracks(snap.song.audioTracks || []);
        ctx.bus.emit('audio:tracksRestored', snap.song.audioTracks || []);
    } else {
        ctx.song.clear();
        ctx.song.clearAudioTracks?.();
    }
    ctx.bus.emit('project:loaded', snap);
}

export function saveNamed(ctx, name) {
    const snap = snapshotProject(ctx, name);
    localStorage.setItem(KEY_PREFIX + name, JSON.stringify(snap));
    const list = listNames();
    if (!list.includes(name)) list.push(name);
    localStorage.setItem(LIST_KEY, JSON.stringify(list));
    return snap;
}

export function loadNamed(ctx, name) {
    const raw = localStorage.getItem(KEY_PREFIX + name);
    if (!raw) return null;
    try {
        const snap = JSON.parse(raw);
        applySnapshot(ctx, snap);
        return snap;
    } catch {
        return null;
    }
}

export function deleteNamed(name) {
    localStorage.removeItem(KEY_PREFIX + name);
    const list = listNames().filter(n => n !== name);
    localStorage.setItem(LIST_KEY, JSON.stringify(list));
}

export function listNames() {
    try { return JSON.parse(localStorage.getItem(LIST_KEY) || '[]'); }
    catch { return []; }
}

// Download the current project as a .json file the user can re-import
// elsewhere (different browser, different machine, sharing with collaborators).
export function exportProjectFile(ctx, name = 'untitled') {
    const snap = snapshotProject(ctx, name);
    const blob = new Blob([JSON.stringify(snap, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.mmproj.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return snap;
}

// Apply a project snapshot from a File / string / object. Also writes the
// snapshot back into localStorage so the project appears in the dropdown.
export async function importProjectFile(ctx, source) {
    let text;
    if (source instanceof Blob) text = await source.text();
    else if (typeof source === 'string') text = source;
    else if (source && typeof source === 'object') text = JSON.stringify(source);
    else throw new Error('importProjectFile: unsupported source');
    const snap = JSON.parse(text);
    applySnapshot(ctx, snap);
    const name = snap.name || 'imported';
    localStorage.setItem(KEY_PREFIX + name, JSON.stringify(snap));
    const list = listNames();
    if (!list.includes(name)) list.push(name);
    localStorage.setItem(LIST_KEY, JSON.stringify(list));
    return snap;
}
